import { API_CONSTANTS, API_HEADERS } from '@/constants/api-constants';
import { getCurrentToken } from '@/services/httpService';

/** Rule API + legacy Athena data-extractor endpoints. */

function getRuleBaseUrl(): string {
  const raw =
    API_CONSTANTS.RULES_BASE_URL ||
    import.meta.env.VITE_API_RULE_URL ||
    'http://localhost:3001/api/v1';
  return String(raw).replace(/\/$/, '');
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function messageFromBody(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'message' in body) {
    const m = (body as { message?: unknown }).message;
    if (m !== undefined && m !== null) return String(m);
  }
  if (typeof body === 'string' && body.trim()) return body;
  return fallback;
}

export async function throwHttpError(res: Response): Promise<never> {
  const body = await readBody(res);
  const msg = messageFromBody(body, res.statusText || `HTTP ${res.status}`);

  if (res.status === 401) {
    throw new Error('Unauthorized: missing or invalid API key.');
  }
  if (res.status === 404) {
    throw new Error('Not found: unknown job ID or resource.');
  }
  if (res.status === 400) {
    throw new Error(msg || 'Bad request.');
  }
  if (res.status === 503) {
    throw new Error(
      msg ||
        'Service unavailable (e.g. consecutive row failures on legacy sync).',
    );
  }
  throw new Error(msg || `Request failed (${res.status}).`);
}

export interface DataExtractionRow {
  row?: number;
  apptId?: string;
  patientId?: string;
  encounterId?: string;
  CPT_CODE?: string;
  ICD_CODE?: string;
  hpiNotes?: {
    hpiNotes?: { content?: string }[];
  };
  assessment?: {
    assessmenttext?: string;
  };
  physicalExam?: {
    summarytext?: string;
  };
  procedureDocumentation?: {
    proceduredocumentation?: {
      proceduredocumentationtext?: string;
    }[];
  };
  appointmentData?: {
    appointmenttype?: string;
    date?: string;
    starttime?: string;
  }[];
  encounterServices?: unknown;
  encounterservices?: unknown;
  csvData?: {
    apptid?: string;
    apptdate?: string;
    patientid?: string;
    claimid?: string;
  };
}

export interface DataExtractionResponse {
  reportName?: string;
  practiceId?: string;
  chunkSize?: number;
  totalRows?: number;
  processedRows?: number;
  failedRows?: number;
  skippedRows?: number;
  data?: DataExtractionRow[];
  errors?: unknown[];
  csv?: string;
  cancelled?: boolean;
  jobId?: string;
}

export interface StartAsyncJobResponse {
  message?: string;
  jobId: string;
  requestUuid?: string;
  status: string;
  timestamp?: string;
}

export interface CsvProcessingProgress {
  totalRows?: number;
  processed?: number;
  failed?: number;
  skipped?: number;
}

export type JobTerminalStatus = 'completed' | 'failed' | 'cancelled';
export type JobActiveStatus = 'pending' | 'processing';
export type JobStatus = JobActiveStatus | JobTerminalStatus | string;

export interface JobProgressResponse {
  data: JobProgressData;
}
export interface JobProgressData {
  jobId?: string;
  status?: JobStatus;
  progressPercentage?: number;
  csvProcessing?: CsvProcessingProgress;
  processedRows?: number;
  failedRows?: number;
  skippedRows?: number;
  errors?: unknown[];
  result?: {
    dataExtractor?: DataExtractionResponse;
  };
  message?: string;
}
export interface CancelJobResponse {
  message?: string;
  jobId?: string;
  status?: string;
}

export interface DataExtractionRequestModel {
  uuid: string;
  practiceId?: string;
  status?: string;
  totalRows?: number;
  processedRows?: number;
  failedRows?: number;
  skippedRows?: number;
  createdAt?: string;
  completedAt?: string | null;
  jobId?: string;
}

export interface DataExtractionRequestsPage {
  totalPages?: number;
  totalElements?: number;
  isFirst?: boolean;
  isLast?: boolean;
  models: DataExtractionRequestModel[];
}

function readStartResponseIds(body: unknown): {
  jobId: string | null;
  requestUuid: string | null;
} {
  if (!body || typeof body !== 'object') {
    return { jobId: null, requestUuid: null };
  }

  const record = body as Record<string, unknown>;
  const fromRecord = (obj: Record<string, unknown>) => ({
    jobId:
      (typeof obj.jobId === 'string' && obj.jobId) ||
      (typeof obj.id === 'string' && obj.id && !obj.uuid ? obj.id : null) ||
      null,
    requestUuid:
      (typeof obj.uuid === 'string' && obj.uuid) ||
      (typeof obj.requestUuid === 'string' && obj.requestUuid) ||
      null,
  });

  const top = fromRecord(record);
  const data = record.data;
  if (data && typeof data === 'object') {
    const nested = fromRecord(data as Record<string, unknown>);
    return {
      jobId: nested.jobId || top.jobId,
      requestUuid: nested.requestUuid || top.requestUuid,
    };
  }

  return top;
}

export async function startExtractAsync(
  tenantId: string,
  practiceId: string,
  file: File,
  options?: { isClaim?: boolean; provider?: string },
): Promise<StartAsyncJobResponse> {
  const token = await getCurrentToken();
  if (!token) {
    throw new Error('Authentication token not found.');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('practiceId', practiceId);
  if (options?.isClaim) {
    formData.append('isClaim', 'true');
  }

  const params = new URLSearchParams();
  if (options?.provider) {
    params.set('provider', options.provider.toUpperCase());
  }
  const url = `${getRuleBaseUrl()}/data-extraction-requests${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      [API_HEADERS.TENANT_ID]: tenantId,
    },
    body: formData,
  });

  if (res.status === 202 || res.ok) {
    const body = await readBody(res);
    const { jobId, requestUuid } = readStartResponseIds(body);
    if (!jobId) {
      throw new Error('Invalid async start response: missing jobId.');
    }
    const record =
      body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
    return {
      jobId,
      requestUuid: requestUuid ?? undefined,
      status: String(record.status ?? 'accepted'),
      message: typeof record.message === 'string' ? record.message : undefined,
      timestamp:
        typeof record.timestamp === 'string' ? record.timestamp : undefined,
    };
  }

  return await throwHttpError(res);
}

export async function getJobProgress(
  jobId: string,
  tenantId: string,
  provider?: string,
): Promise<JobProgressResponse> {
  const token = await getCurrentToken();
  if (!token) {
    throw new Error('Authentication token not found.');
  }

  const params = new URLSearchParams();
  if (provider) {
    params.set('provider', provider.toUpperCase());
  }
  const base = `${getRuleBaseUrl()}/data-extraction-requests/jobs/${encodeURIComponent(jobId)}`;
  const url = `${base}${params.toString() ? `?${params.toString()}` : ''}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    [API_HEADERS.TENANT_ID]: tenantId,
  };
  const res = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (res.ok) {
    return ((await readBody(res)) || {}) as JobProgressResponse;
  }

  return await throwHttpError(res);
}

export async function submitExtractionRecords(
  tenantId: string,
  requestId: string,
  payload: DataExtractionResponse,
  provider?: string,
): Promise<void> {
  const token = await getCurrentToken();
  if (!token) {
    throw new Error('Authentication token not found.');
  }

  const params = new URLSearchParams();
  if (provider) {
    params.set('provider', provider.toUpperCase());
  }
  const base = `${getRuleBaseUrl()}/data-extraction-requests/${encodeURIComponent(requestId)}/records`;
  const url = `${base}${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      [API_HEADERS.TENANT_ID]: tenantId,
    },
    body: JSON.stringify(payload),
  });

  if (res.ok) return;
  return await throwHttpError(res);
}

export async function listExtractionRequests(
  tenantId: string,
  options?: { pageNo?: number; pageSize?: number; provider?: string },
): Promise<DataExtractionRequestsPage> {
  const token = await getCurrentToken();
  if (!token) {
    throw new Error('Authentication token not found.');
  }

  const pageNo = options?.pageNo ?? 0;
  const pageSize = options?.pageSize ?? 10;
  const params = new URLSearchParams({
    pageNo: String(pageNo),
    pageSize: String(pageSize),
  });
  if (options?.provider) {
    params.set('provider', options.provider.toUpperCase());
  }

  const url = `${getRuleBaseUrl()}/data-extraction-requests?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      [API_HEADERS.TENANT_ID]: tenantId,
    },
  });

  if (!res.ok) {
    return await throwHttpError(res);
  }

  const body = (await readBody(res)) as {
    data?: DataExtractionRequestsPage;
  } | null;

  return {
    totalPages: body?.data?.totalPages ?? 0,
    totalElements: body?.data?.totalElements ?? 0,
    isFirst: body?.data?.isFirst ?? true,
    isLast: body?.data?.isLast ?? true,
    models: Array.isArray(body?.data?.models) ? body!.data!.models : [],
  };
}

export async function cancelExtraction(
  jobId: string,
  tenantId: string,
  provider?: string,
): Promise<CancelJobResponse> {
  const token = await getCurrentToken();
  if (!token) {
    throw new Error('Authentication token not found.');
  }

  const params = new URLSearchParams();
  if (provider) {
    params.set('provider', provider.toUpperCase());
  }
  const base = `${getRuleBaseUrl()}/data-extraction-requests/cancel/${encodeURIComponent(jobId)}`;
  const url = `${base}${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      [API_HEADERS.TENANT_ID]: tenantId,
    },
  });

  if (res.ok) {
    return ((await readBody(res)) || {}) as CancelJobResponse;
  }

  return await throwHttpError(res);
}

export const dataExtractionService = {
  startExtractAsync,
  getJobProgress,
  submitExtractionRecords,
  listExtractionRequests,
  cancelExtraction,
};
